import type { PageConfig } from "@norkron/web-page-layout";
import { BODY_PAGE_CONFIG } from "../../lib/brandedPreview";

/**
 * `canvasPageConfig` — the `pl_layout` page template the PAGINATED EDIT surface
 * lays out with.
 *
 * WHY THIS EXISTS (the bug it fixes). `createWasmRelayout({ config })` is
 * OPTIONAL upstream, and `<CanvasElaborateEditor>` omitted it. An absent config
 * resolves to `web-page-layout`'s `A4_DEFAULT`: 54pt margins on all four sides,
 * `headingSpaceBeforePt`/`headingSpaceAfterPt` all ZERO and no `bodyLeadingPt`.
 * Those zeros reach `pdfz/src/writer.zig` where `head_sb`/`head_sa` resolve to 0
 * and `page.setSpaceNext` is never called — so headings butted straight into the
 * body text and every line packed at the flex default 13pt leading. That is the
 * "everything is crushed together" the paginated view shipped with. The branded
 * PDF preview never had it because `brandedPreview.ts` DOES pass a config; this
 * module gives the edit surface the same treatment.
 *
 * REVIEW == EXPORT. The geometry is {@link BODY_PAGE_CONFIG} — the SAME Cerebr8
 * plate `Body` page-master the branded preview and the exported PDF lay out with
 * — so a reviewer editing on canvas sees the client's pagination. It is spread,
 * not retyped, so the two can never drift.
 *
 * THE TWO DELIBERATE OVERRIDES (edit-only, and they are not cosmetic):
 *  · `justify: true` — `pdfz/src/wasm/layout_pipeline.zig`'s `forceJustify` is
 *    the SOLE wrap strategy that carries per-line `(block_ord, byte_offset)`
 *    provenance. The read-only branded preview sets `justify: false` because it
 *    needs no caret; the EDIT surface does. Copying the preview's value verbatim
 *    would leave every run's `block_ord` unknown and no caret could enter the
 *    document at all.
 *  · `glyphGranular: true` — per-glyph advances are what caret x↔byte math is
 *    computed from.
 * Both are already A4_DEFAULT's values; they are restated explicitly (not
 * inherited) so a future edit to the branded config cannot silently disarm the
 * caret. (`measuredGreedy` rides through inert: `pdfz/src/flex.zig:5737` only
 * consults it on the `greedy_compat` strategy, which `justify: true` never takes.)
 *
 * Consequence, stated honestly: the edit surface wraps JUSTIFIED while the export
 * wraps ragged, so line breaks can differ from the PDF even though the page box,
 * leading and heading rhythm match. Closing that needs the pdfz FR that stamps
 * provenance on the greedy path — it is not closable consumer-side.
 */

/**
 * Top margin, in points. 99pt is the plate `Body` value: it reserves the band the
 * branded PDF paints its running header into. The paginated EDIT surface CANNOT
 * paint a running header (the PLCF wire carries no running-element channel at
 * all), so on canvas the band renders as empty space at the top of each page.
 *
 * OWNER-VISIBLE TRADE-OFF — this is the flip point:
 *  · 99 (chosen) — canvas and PDF paginate IDENTICALLY: a reviewer's page breaks
 *    are the client's page breaks. Cost: an empty reserved band on screen.
 *  · 72 — tighter-looking pages, at the cost of canvas and PDF paginating
 *    DIFFERENTLY. In a REVIEW product, a reviewer seeing different page breaks
 *    from the delivered document is the worse failure, so 99 wins by default.
 */
export const CANVAS_MARGIN_TOP_PT = 99;

/** The page template `<CanvasElaborateEditor>` hands `createWasmRelayout`. */
export const CANVAS_EDIT_PAGE_CONFIG: PageConfig = {
  ...BODY_PAGE_CONFIG,
  marginTopPt: CANVAS_MARGIN_TOP_PT,
  // Caret-critical — see the module doc. Never inherit these.
  justify: true,
  glyphGranular: true,
};
